import { useDraggable, useDroppable } from '@dnd-kit/core';
import classNames from 'classnames';
import { ChangeEvent } from 'react';
import Skeleton from 'react-loading-skeleton';
import { getItemAvailabilityLabelOptions } from '../../helpers/menuGroupEdit';
import menuGroupEditStore from '../../store/menuGroupEditStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { appStore } from 'src/mobx/appStore';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { getOrderTypeIconsNames, stringTransform } from 'src/utils/helpers';
import { floatNumbersValidation } from 'src/utils/validation';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Input from 'src/view/components/Controls/Input/Input';
import Image from 'src/view/components/Image/Image';
import OrderTypes from 'src/view/components/OrderTypes/OrderTypes';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { IMenuGroupProduct } from 'src/view/screens/MenuManagementScreen/screens/MenuGroupsScreen/types/menuGroups';
import Label from '~/view/components/Controls/Label';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';
import './style.scss';

interface IProps {
  id: number;
  item?: IMenuGroupProduct;
  className?: string;
  dragIconClassName?: string;
  isActiveSearch?: boolean;
  isTaker360Enabled?: boolean;
  columnsWidth: {
    main: number;
    secondary: number;
  };
}

const MenuGroupItemsListRow = (props: IProps): JSX.Element => {
  const { attributes, isDragging, listeners, setNodeRef: draggableRef } = useDraggable({ id: props.id.toString() });
  const { isOver, setNodeRef: droppableRef } = useDroppable({ id: props.id.toString() });

  const onSelectItem = () => {
    if (!props.item) return;
    modalStore.openMenuGroupItemModal({ item: props.item, isTaker360Enabled: props.isTaker360Enabled, onPriceSave });
  };

  const onDeleteItem = () => {
    menuGroupEditStore.excludeCategoryItem(props.item?.category?.id, props.item?.id);
  };

  const onPriceSave = (value: string) => {
    if (!props.item) return;
    menuGroupEditStore.changeItemPrice(props.item, props.item.category?.id, value);
  };

  const onInputPriceChange = (event: ChangeEvent<HTMLInputElement>) => {
    const price = stringTransform(event.target.value);

    if (props.item && floatNumbersValidation(price, 1)) {
      menuGroupEditStore.changeItemPrice(props.item, props.item.category?.id, price);
    }
  };

  const renderItemAvailability = (): JSX.Element => {
    if (!props.item) return <Skeleton width={50} />;

    const labelOptions = getItemAvailabilityLabelOptions(props.item);
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Label {...labelOptions} />;
  };

  const renderPrice = (): JSX.Element => {
    if (!props.item) return <Skeleton width={75} />;

    const isVatIncluded = !!appStore.settings?.vat_included;
    return (
      <div className="d-flex align-items-center">
        <p className="family-medium text-size-16 mr-3">{props.item.price}</p>
        <p className="family-medium text-size-12 color-gray2 mr-2">{localeStore.t(isVatIncluded ? 'vat_incl' : 'vat_excl')}</p>
        <TooltipIcon message={isVatIncluded ? 'vat_included' : 'vat_excluded'} />
      </div>
    );
  };

  const renderTaker360Price = (): JSX.Element => {
    if (!props.item) return <Skeleton width={100} />;
    if (props.item?.sizes.length || props.item.type) {
      return <p className="family-medium text-size-16 text-center">&ndash;</p>;
    }

    return (
      <Input
        className="menu-group-items-list-row__price input"
        value={props.item.menu_group_price ?? props.item.price}
        onChange={onInputPriceChange}
      />
    );
  };

  const orderTypeIconNames = getOrderTypeIconsNames(props.item?.order_type);
  const disabled: boolean = menuGroupEditStore.loadingOnFetch || menuGroupEditStore.loadingOnSave;

  const className = classNames('menu-group-items-list-row mb-4 pr-3', props.className, { 'menu-group-items-list-row__image-drag_droppable': isOver });
  const dragBtnClassName = classNames('menu-group-items-list-row__image-drag', props.dragIconClassName, { disabled });

  const { columnsWidth } = props;

  return (
    <div className={className} key={props.item?.id} ref={isDragging ? draggableRef : droppableRef}>
      <div className={`d-flex flex-row align-items-center cursor-pointer w-${columnsWidth.main}`} onClick={onSelectItem}>
        <div className="menu-group-items-list-row__image-wrapper mr-4">
          <Image
            refreshing={!props.item}
            src={props.item?.image || ''}
            alt="img"
            wrapperClassName="menu-group-items-list-row__image"
            className="full-height"
            aspectRatio="none"
          />
          {props.item && !props.isActiveSearch ? (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <button className={dragBtnClassName} {...listeners} {...attributes}>
              <Icon name={Icons.DragHandHorizontal} size={14} className="menu-group-items-list-row__image-drag-icon" />
            </button>
          ) : null}
        </div>
        <div className="menu-group-items-list-row__name full-width full-height d-flex justify-content-center align-items-start flex-column pr-4">
          {props.item ? <Text type={TEXT.LIST_ROW} text={props.item.name} /> : <Skeleton width={100} />}
          {props?.item?.is_donation ? <Text type={TEXT.DONATABLE_IN_LIST} text="donatable" /> : null}
        </div>
      </div>
      {props.isActiveSearch && (
        <div className={`d-flex flex-row align-items-center pr-4 w-${columnsWidth.secondary}`}>
          {props.item ? <Text type={TEXT.LIST_ROW} text={props.item.category?.name} /> : <Skeleton width={50} />}
        </div>
      )}
      <div className={`d-flex flex-row align-items-center w-${columnsWidth.secondary}`}>
        {props.item ? <OrderTypes types={orderTypeIconNames} /> : <Skeleton width={50} />}
      </div>
      <div className={`d-flex flex-row align-items-center w-${columnsWidth.secondary}`}>{renderPrice()}</div>
      {props.isTaker360Enabled && (
        <div className={`d-flex flex-row align-items-center justify-content-between w-${columnsWidth.secondary}`}>{renderTaker360Price()}</div>
      )}
      <div className={`d-flex flex-row align-items-center justify-content-between w-${columnsWidth.secondary}`}>
        {renderItemAvailability()}
        {props.item ? (
          <Icon name={Icons.Close} className="color-gray2 cursor-pointer" disabled={disabled} onPress={onDeleteItem} disabledClassName="disabled" />
        ) : (
          <Skeleton width={25} />
        )}
      </div>
    </div>
  );
};

export default MenuGroupItemsListRow;
